/* Ounce — what every page shares, on Shopify. The basket is Shopify's cart (the AJAX cart API); checkout is Shopify's.
   The basket drawer and the phone menu are <dialog>s. Shopify's standard storefront events go out where things happen,
   and Shopify's cart actions (what apps and shopping agents call) update and open our drawer instead of reloading.
   Events and actions: https://shopify.dev/docs/api/storefront-events-and-actions

   Each section's own behaviour is in its {% javascript %} (the menu in sections/header.liquid, the brochure in
   blocks/_brochure.liquid, the product page in blocks/_buy-box.liquid …). Shopify loads those after this file, and they
   use what it shares on window.Ounce. */
(() => {
  'use strict';
  const S = document.currentScript, ROOT = S.dataset.root || '/';
  const TEMPLATE = S.dataset.template || '', CURRENCY = S.dataset.currency || 'GBP';
  const BAG = 'cart-drawer';   // the basket drawer's section (sections/cart-drawer.liquid), rendered statically by the layout
  const $ = (s, r = document) => r.querySelector(s), $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const money = p => '£' + (p % 100 ? (p / 100).toFixed(2) : p / 100);
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches, fine = matchMedia('(pointer: fine)').matches;
  const pointer = e => e.detail > 0;   // a click from a mouse or a finger, not Enter or Space

  /* Shopify's standard events library, from Shopify's CDN. It loads without holding anything up; until it arrives,
     or if it never does, the site works the same and no events go out. */
  let SE = null;
  const seReady = import('https://cdn.shopify.com/storefront/standard-events.js').then(m => {
    SE = m;
    // <ounce-view-event> sends shopify:product:view, collection:view and cart:view for what it wraps (see the sections)
    if (!customElements.get('ounce-view-event')) customElements.define('ounce-view-event', m.createViewEventElement());
    bagPayload();
    return m;
  }).catch(() => null);

  /* reveal on enter; in the theme editor a section that redraws shows at once */
  const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' });
  $$('.r').forEach(el => io.observe(el));
  document.addEventListener('shopify:section:load', e => $$('.r', e.target).forEach(el => el.classList.add('in')));

  /* paw marks (inside an open menu, so they show above it) */
  if (!still) {
    const PC = ['--feed-l', '--wear-l', '--play-l', '--groom-l']; let pi = 0;
    document.addEventListener('pointerdown', e => {
      if (e.target.closest('.drawer,.qty,.seg')) return;
      const p = document.createElement('i'); p.className = 'pawmark'; p.style.setProperty('--pc', `var(${PC[pi++ % 4]})`);
      p.style.left = (e.clientX - 12) + 'px'; p.style.top = (e.clientY - 12) + 'px'; p.style.transform = `rotate(${(Math.random() * 40 - 20) | 0}deg)`;
      (e.target.closest('dialog[open]') || document.body).appendChild(p); setTimeout(() => p.remove(), 1900);
    }, { passive: true });
  }

  /* the basket drawer and the phone menu are modal <dialog>s: the page behind is inert, Esc closes, focus goes back to
     the button that opened them. .open slides them in (CSS); closing slides them out, then close() removes them. */
  const SLIDE = 450;   // ms: the longest slide in the CSS (.4s) and a little over
  const lockScroll = () => { document.body.style.overflow = $('dialog.open') ? 'hidden' : ''; };
  function modal(dlg, closeSel) {
    if (!dlg) return { open() { }, close() { } };
    let opener = null, timer = 0, byPointer = false;
    const open = (from, pointer) => {
      clearTimeout(timer);
      if (!dlg.open) { opener = from || document.activeElement; dlg.showModal(); void dlg.offsetWidth; }
      dlg.classList.add('open'); lockScroll();
      $(closeSel, dlg)?.focus({ focusVisible: !pointer });
    };
    const close = pointer => {
      if (!dlg.open || !dlg.classList.contains('open')) return;
      byPointer = !!pointer;
      dlg.classList.remove('open'); lockScroll();
      timer = setTimeout(() => dlg.close(), SLIDE);
    };
    dlg.addEventListener('close', () => {   // also when the browser closes it without asking (a second Esc)
      clearTimeout(timer); dlg.classList.remove('open'); lockScroll();
      const a = document.activeElement;
      if (opener?.isConnected && (!a || a === document.body || dlg.contains(a))) opener.focus({ preventScroll: true, focusVisible: !byPointer });
      opener = null; byPointer = false;
    });
    dlg.addEventListener('cancel', e => { e.preventDefault(); close(); });
    dlg.addEventListener('click', e => {   // a click on the backdrop lands on the dialog itself, outside its box
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close(true);
    });
    return { open, close, el: dlg };
  }
  const drawer = $('dialog.drawer'), bag = modal(drawer, '[data-close-bag]');

  /* basket: Shopify's cart */
  const api = (path, body) => fetch(`${ROOT}${path}`, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { headers: { Accept: 'application/json' } })
    .then(async r => { const j = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(j.description || j.message || r.statusText), { status: r.status }); return j; });
  let cart = { items: [], item_count: 0, total_price: 0 };
  let toastT;
  const toast = html => {
    const t = $('[data-toast]'); if (!t) return;
    const host = $('dialog.open') || document.body; if (t.parentNode !== host) host.append(t);   // above an open basket
    t.innerHTML = html; t.classList.add('on'); clearTimeout(toastT); toastT = setTimeout(() => t.classList.remove('on'), 3200);
  };

  /* what shopify:cart:view carries when the drawer opens: the cart as it is now, not as it was when the page loaded
     (the page's payload says null until the buyer's first cart exists; /cart.js has a token either way) */
  function bagPayload() {
    const v = drawer && $('ounce-view-event', drawer);
    if (!v || !SE || !cart.token) return;
    if (!cart.item_count && v.getAttribute('view-event-payload')?.includes('"cart":null')) return;
    v.setAttribute('view-event-payload', JSON.stringify({ context: 'dialog', cart: SE.CartViewEvent.createCartFromAjaxResponse(cart) }));
  }

  /* A change the site makes to the cart goes out as shopify:cart:lines-update when it starts; its promise settles with
     the cart afterwards. A change the cart declines settles with userErrors; a request that fails rejects and also
     sends shopify:cart:error. */
  async function cartChange(el, action, context, lines, run) {
    let d = null;
    if (SE && el) {
      d = SE.CartLinesUpdateEvent.createPromise();
      const ev = new SE.CartLinesUpdateEvent({ action, context, lines, promise: d.promise });
      ev.promise.catch(() => { });   // a failure reaches listeners as shopify:cart:error
      el.dispatchEvent(ev);
    }
    try {
      const c = await run();
      d?.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(c) });
      return c;
    } catch (err) {
      if (d && err.status >= 400 && err.status < 500) {
        const c = await api('cart.js').catch(() => null);
        d.resolve({ cart: c && SE.CartLinesUpdateEvent.createCartFromAjaxResponse(c), userErrors: [{ code: 'INVALID', field: ['lines'], message: err.message }] });
      } else if (d) {
        el.dispatchEvent(new SE.CartErrorEvent({ error: err.message, code: 'SERVICE_UNAVAILABLE' }));
        d.reject(err);
      }
      throw err;
    }
  }

  /* The cart as it is now: the counts in the header follow it, and ounce:cart tells the page (the product page's
     "You have all of them"). */
  function setCart(c = cart) {
    cart = c;
    $$('[data-count]').forEach(el => { el.textContent = c.item_count; c.item_count ? el.removeAttribute('data-zero') : el.setAttribute('data-zero', ''); });
    bagPayload();
    document.dispatchEvent(new CustomEvent('ounce:cart', { detail: c }));
  }

  /* The drawer redrawn from its section, as Shopify rendered it with the cart change (bundled section rendering) or
     on request: the lines and the footer are swapped; the dialog, its state and its view event stay. */
  function drawBag(sections) {
    const html = sections && sections[BAG];
    if (!html || !drawer) return;
    const next = new DOMParser().parseFromString(html, 'text/html');
    for (const sel of ['[data-lines]', '[data-bag-foot]']) {
      const from = $(sel, next), to = $(sel, drawer);
      if (from && to) to.innerHTML = from.innerHTML;
    }
  }
  const fetchBag = () => api(`?sections=${BAG}`).then(drawBag);

  api('cart.js').then(c => setCart(c)).catch(() => setCart());

  /* Shopify's cart actions, for apps and shopping agents: updateCart writes the cart as Shopify does, then our drawer
     redraws from its section and the counts from /cart.js instead of the page reloading; openCart opens the drawer.
     Only the first configuration of an action counts, which is why this file loads before content_for_header. The
     actions exist from DOMContentLoaded. */
  function configureActions() {
    const A = window.Shopify?.actions;
    A?.updateCart?.configure?.({
      eventTarget: meta => (meta.type === 'shopify:cart:lines-update' && meta.action === 'add' && $('.pdp')) || drawer,
      async handler(defaultHandler) {
        const result = await defaultHandler();
        try { const [c] = await Promise.all([api('cart.js'), fetchBag()]); setCart(c); } catch { /* the drawer catches up on the next change */ }
        return { ...result, detail: { ...result?.detail, handledBy: 'ounce' } };
      },
    });
    A?.openCart?.configure?.({ async handler() { bag.open(); } });
  }
  document.addEventListener('DOMContentLoaded', async () => {
    configureActions();
    const se = await seReady;
    se?.PageViewEvent && document.dispatchEvent(new se.PageViewEvent({ page: { template: TEMPLATE, title: document.title, url: location.href } }));
  });

  window.Ounce = {
    $, $$, money, esc, still, fine, pointer, modal, bag, api, toast, cartChange, setCart, drawBag, BAG, CURRENCY,
    get cart() { return cart; },
    get SE() { return SE; },
  };
})();
